package com.schoolconnect.app.datasource.network.response

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

@Serializable
data class StudentExamResultResponse(
    val id: String? = null,
    @SerialName("exam_id") val examId: String? = null,
    @SerialName("student_id") val studentId: String? = null,
    @SerialName("student_name") val studentName: String? = null,
    @SerialName("roll_number") val rollNumber: String? = null,
    @SerialName("overall_grade") val overallGrade: String? = null,
    @SerialName("overall_rank") val overallRank: Int? = null,
    @SerialName("total_marks_obtained") val totalMarksObtained: Double? = null,
    @SerialName("total_max_marks") val totalMaxMarks: Double? = null,
    val percentage: Double? = null,
    val notes: String? = null,
    @SerialName("is_fully_absent") val isFullyAbsent: Boolean? = null
)

@Serializable
data class ExamResultsListResponse(
    val count: Int? = null,
    val items: List<StudentExamResultResponse>? = null,
    @SerialName("exam_id") val examId: String? = null
)

@Serializable
data class StudentExamResultRecordResponse(
    @SerialName("exam_id") val examId: String? = null,
    @SerialName("student_id") val studentId: String? = null,
    @SerialName("subject_id") val subjectId: String? = null,
    @SerialName("marks_obtained") val marksObtained: Double? = null,
    @SerialName("internal_score") val internalScore: Double? = null,
    @SerialName("main_score") val mainScore: Double? = null,
    @SerialName("is_absent") val isAbsent: Boolean? = null,
    @SerialName("teacher_remarks") val teacherRemarks: String? = null,
    val grade: String? = null,
    val rank: Int? = null,
    val notes: String? = null,
    val id: String? = null,
    @SerialName("created_at") val createdAt: String? = null
)

@Serializable
data class StudentExamResultRecordsResponse(
    val count: Int? = null,
    val items: List<StudentExamResultRecordResponse>? = null
)

@Serializable
data class ExamResultSheetResponse(
    @SerialName("exam_id") val examId: String? = null,
    @SerialName("student_id") val studentId: String? = null,
    @SerialName("subject_id") val subjectId: String? = null,
    @SerialName("marks_obtained") val marksObtained: Double? = null,
    @SerialName("internal_score") val internalScore: Double? = null,
    @SerialName("main_score") val mainScore: Double? = null,
    @SerialName("is_absent") val isAbsent: Boolean? = null,
    val grade: String? = null,
    val rank: Int? = null,
    val notes: String? = null,
    val id: String? = null,
    @SerialName("student_name") val studentName: String? = null,
    @SerialName("roll_number") val rollNumber: String? = null,
    @SerialName("total_marks") val totalMarks: Double? = null,
    @SerialName("total_percentage") val totalPercentage: Double? = null
)

@Serializable
data class ExamResultsSummaryResponse(
    @SerialName("exam_id") val examId: String? = null,
    val title: String? = null,
    @SerialName("class_section_id") val classSectionId: String? = null,
    val subjectId: String? = null,
    val count: Int? = null,
    val results: List<ExamResultSheetResponse>? = null
)

@Serializable
data class ExamInsightsResponse(
    @SerialName("exam_id") val examId: String? = null,
    @SerialName("exam_name") val examName: String? = null,
    @SerialName("class_section_id") val classSectionId: String? = null,
    @SerialName("class_name") val className: String? = null,
    @SerialName("section_name") val sectionName: String? = null,
    @SerialName("start_date") val startDate: String? = null,
    val status: String? = null,
    @SerialName("max_marks") val maxMarks: Double? = null,
    val published: Boolean? = null,
    @SerialName("total_students") val totalStudents: Int? = null,
    @SerialName("overall_percentage_average") val overallPercentageAverage: Double? = null,
    @SerialName("top_5_students") val top5Students: List<ExamInsightStudentResponse>? = null,
    @SerialName("bottom_5_students") val bottom5Students: List<ExamInsightStudentResponse>? = null,
    @SerialName("performance_buckets") val performanceBuckets: List<PerformanceBucketResponse>? = null
)

@Serializable
data class ExamInsightStudentResponse(
    val rank: Int? = null,
    @SerialName("student_id") val studentId: String? = null,
    @SerialName("student_name") val studentName: String? = null,
    @SerialName("roll_number") val rollNumber: String? = null,
    @SerialName("total_marks") val totalMarks: Double? = null,
    val percentage: Double? = null
)

@Serializable
data class PerformanceBucketResponse(
    val bucket: String? = null,
    @SerialName("display_value") val displayValue: String? = null,
    val count: Int? = null,
    val percentage: Double? = null
)

@Serializable
data class ExamPublishResponse(
    @SerialName("exam_id") val examId: String? = null,
    val status: String? = null,
    val published: Boolean? = null,
    @SerialName("total_students") val totalStudents: Int? = null,
    @SerialName("fully_graded") val fullyGraded: Int? = null,
    @SerialName("advisory_warnings") val advisoryWarnings: List<String>? = null
)

@Serializable
data class ExamResultBulkEntryResponse(
    @SerialName("exam_id") val examId: String? = null,
    @SerialName("class_section_id") val classSectionId: String? = null,
    @SerialName("processed_count") val processedCount: Int? = null,
    val status: String? = null
)
